/*
  Admin login page. Lives at /_vh-admin. No links from anywhere in the app —
  you navigate here by typing the URL.
*/
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, Eye, EyeOff } from "lucide-react";
import { adminApi } from "../lib/adminApi";

export default function AdminLoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(true);
  const nav = useNavigate();

  // If already authenticated, skip straight to the dashboard.
  useEffect(() => {
    let active = true;
    adminApi.me()
      .then(() => { if (active) nav("/_vh-admin/dashboard", { replace: true }); })
      .catch(() => { if (active) setChecking(false); });
    return () => { active = false; };
  }, [nav]);

  async function submit(e) {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await adminApi.login(email.trim().toLowerCase(), password);
      nav("/_vh-admin/dashboard", { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  if (checking) {
    return <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg)", color: "var(--text-muted)" }}>…</div>;
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ background: "var(--bg)" }}>
      <form onSubmit={submit} className="w-full max-w-sm" data-testid="admin-login-form">
        <div className="flex items-center gap-2 mb-6">
          <div className="h-9 w-9 rounded-lg bg-[color:var(--primary-soft)] flex items-center justify-center">
            <Shield className="h-5 w-5 text-[color:var(--primary-ink)]" />
          </div>
          <div>
            <div className="label-caps">Restricted</div>
            <h1 className="font-display font-black tracking-tighter text-xl">Admin sign-in</h1>
          </div>
        </div>

        <div className="card-tactile rounded-2xl p-6 space-y-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              data-testid="admin-email"
              className="mt-1 w-full px-3 py-2 rounded-md border bg-[color:var(--surface)] text-[color:var(--text-main)] focus:outline-none focus:border-[color:var(--primary)]"
              style={{ borderColor: "var(--border-subtle)" }}
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Password</label>
            <div className="relative">
              <input
                type={showPw ? "text" : "password"}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                data-testid="admin-password"
                className="mt-1 w-full px-3 py-2 pr-10 rounded-md border bg-[color:var(--surface)] text-[color:var(--text-main)] focus:outline-none focus:border-[color:var(--primary)]"
                style={{ borderColor: "var(--border-subtle)" }}
              />
              <button type="button" tabIndex={-1} onClick={() => setShowPw(s => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[color:var(--text-muted)] hover:text-[color:var(--text-main)]"
                aria-label={showPw ? "Hide password" : "Show password"}
              >
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          {error && (
            <div data-testid="admin-login-error" className="text-sm rounded-md px-3 py-2" style={{ background: "color-mix(in srgb, var(--accent) 12%, transparent)", color: "var(--accent-ink)" }}>
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={busy}
            data-testid="admin-login-submit"
            className="w-full h-11 rounded-lg font-semibold text-sm text-white disabled:opacity-50"
            style={{ background: "var(--primary)" }}
          >
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </div>

        <p className="mt-4 text-xs text-center" style={{ color: "var(--text-muted)" }}>
          VeriHandi · Restricted area
        </p>
      </form>
    </div>
  );
}
